import type { RouterContextType } from "@/api/permissions/permissions.api";
// SERV-1932: Upload Photos hidden in the bare-bones visit form (restore with the render below).
// import { UploadPhotos } from "@/components/features/WorkOrders/NewVisit/UploadPhotos";
import { VisitCreatedBy } from "@/components/features/WorkOrders/NewVisit/VisitCreatedBy";
import { VisitDetails } from "@/components/features/WorkOrders/NewVisit/VisitDetails";
import { Button } from "@/components/ui/Button/Button";
import { useVisitForm, VISIT_FIELD_ORDER } from "@/hooks/work-order/useVisitForm";
import { authGuardWithPermissions } from "@/utils/authGuard";
import {
  leaveRecordTenant,
  recordTenantSearchSchema,
  syncRecordTenant,
} from "@/utils/recordTenantRoute";
import { scrollToFirstError } from "@/utils/scrollToFirstError";
import { showErrorAlert } from "@/utils/responseHandler";
import { Box, Flex, Stack, Title } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import {
  createFileRoute,
  retainSearchParams,
  useNavigate,
  useLocation,
} from "@tanstack/react-router";
import { FormProvider } from "react-hook-form";
import type { FieldErrors } from "react-hook-form";
import styles from "./add-visit.module.scss";

export const Route = createFileRoute("/work-orders/$workOrderId/add-visit/")({
  component: AddVisit,
  validateSearch: recordTenantSearchSchema.parse,
  search: { middlewares: [retainSearchParams(["tenant"])] },
  onEnter: syncRecordTenant,
  onStay: syncRecordTenant,
  onLeave: leaveRecordTenant,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function AddVisit() {
  const { methods, onSubmit, isAddVisitLoading } = useVisitForm();
  const navigate = useNavigate();
  const { workOrderId } = Route.useParams();
  const location = useLocation();
  const previousTab = (location.state as { previousTab?: string })?.previousTab || "overview";

  const onInvalid = (errors: FieldErrors) => {
    showErrorAlert("Please fill in all required fields.", "Validation Error");
    scrollToFirstError(errors, methods.setFocus, VISIT_FIELD_ORDER);
  };

  const handleClose = () => {
    navigate({
      to: "/work-orders/$workOrderId",
      params: { workOrderId },
      search: (prev) => ({ ...prev, tab: previousTab }),
    });
  };

  return (
    <FormProvider {...methods}>
      <Box className={styles.section}>
        <Flex justify={"space-between"} align={"center"} className={styles.header}>
          <Title order={3} className={styles.title}>
            New Visit
          </Title>
          <IconX size={16} className={styles.closeIcon} onClick={handleClose} aria-label="Close" />
        </Flex>
        <Box className={styles.container}>
          <Flex gap="xl" style={{ width: "100%" }}>
            <Stack gap="lg" style={{ flex: 1 }}>
              <VisitDetails workOrderId={workOrderId} />
              {/* SERV-1932: Hide Upload Photos for Bare Bones MVP. Restore by uncommenting
                  this render and the import above. */}
              {/* <UploadPhotos /> */}
            </Stack>
            <Stack gap="lg" style={{ flex: 0.5 }}>
              <VisitCreatedBy />
            </Stack>
          </Flex>
        </Box>
        <Box className={styles.footer}>
          <Box className={styles.footerContent}>
            <Button
              variant="primary"
              onClick={methods.handleSubmit(onSubmit, onInvalid)}
              loading={methods.formState.isSubmitting || isAddVisitLoading}
              disabled={methods.formState.isSubmitting || isAddVisitLoading}
            >
              Add Visit Details
            </Button>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  );
}
